import {
  AlertTriangleIcon,
  CloudRainIcon,
  DropletsIcon,
  GraduationCapIcon,
  LeafIcon,
  LayersIcon,
  RecycleIcon,
  ShipWheelIcon,
  WavesIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  context: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Foundations of Hydrology: Understanding Water Systems',
    subtitle: 'The foundation your students need to understand everything else',
    context:
      'Your first introduction to hydrology can be transformative or tedious. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'What is hydrology and why it matters for society',
      'Major branches and real-world applications of hydrology',
      'Global distribution and cycling of water',
      'Physical properties and states of water',
      'Hydrological variables and fundamental concepts',
      'Water storage and movement across Earth systems',
      'Connections between water, climate, and landscapes',
      'Methods used in hydrological research',
      'Clear explanations with real-world examples',
    ],
    result:
      'Your students understand hydrology as a practical science that shapes landscapes, climates, and human societies — not an impossible list of terms to memorize.',
    icon: DropletsIcon,
  },
  {
    number: '2',
    title: 'The Hydrological Cycle & Water Balance: From Evaporation to Groundwater',
    subtitle: 'Everything your students need to know about how water moves through natural systems',
    context:
      'This is where many students struggle. This volume gives you the materials to help them truly understand the complete water cycle and water-balance calculations.',
    items: [
      'Complete components of the hydrological cycle',
      'Evaporation, transpiration, and evapotranspiration',
      'Condensation and atmospheric moisture formation',
      'Precipitation processes and formation',
      'Interception and surface storage',
      'Infiltration, percolation, and subsurface flow',
      'Surface runoff and streamflow generation',
      'Groundwater recharge and discharge mechanisms',
      'Water storage in natural systems',
      'Introduction to water-balance calculations',
      'Human alterations to natural cycles',
      '12 worked water-balance calculation examples',
    ],
    result: 'Your students learn to understand and calculate water movement with method and confidence.',
    icon: RecycleIcon,
  },
  {
    number: '3',
    title: 'Precipitation, Climate & Hydrometeorology: Understanding Water from the Sky',
    subtitle: 'How to teach atmospheric water processes clearly and practically',
    context:
      'Precipitation and atmospheric processes are fascinating but complex. This volume gives you everything needed so your students understand how water forms clouds and falls as rain or snow.',
    items: [
      'Atmospheric processes related to water',
      'Formation and classification of precipitation types',
      'Rainfall, snowfall, hail and other precipitation forms',
      'Precipitation measurement methods and instruments',
      'Spatial and temporal rainfall distribution patterns',
      'Intensity, duration and frequency relationships',
      'Hydrometeorological data interpretation',
      'Weather systems and water availability',
      'Climate variability and hydrological processes',
      'Extreme precipitation events and analysis',
      'Drought formation and monitoring methods',
      'Climate change impacts on the global water cycle',
      '10 practical case studies ready for classroom use',
    ],
    result:
      'Your students understand why precipitation patterns are critical to water resources and how climate shapes water availability.',
    icon: CloudRainIcon,
  },
  {
    number: '4',
    title: 'Surface Water, Rivers & Watersheds: Understanding Flowing Systems',
    subtitle: 'Water systems and watershed processes explained comprehensively',
    context:
      'Surface water systems and watersheds are complex but fundamental. This volume breaks them down without losing clarity.',
    items: [
      'Surface-water systems and their characteristics',
      'Watersheds and drainage basins fundamentals',
      'Watershed boundaries and drainage patterns',
      'River network development and formation',
      'Streamflow, discharge, and measurement',
      'Runoff-generation processes and mechanisms',
      'Hydrographs and river response to rainfall',
      'River channels, floodplains, and erosion',
      'Sediment transport and depositional processes',
      'Lakes, reservoirs, and wetland systems',
      'Land-use impacts on watershed hydrology',
      'Watershed conservation and management strategies',
      '10 practical watershed application case studies',
    ],
    result:
      'Your students understand how rivers shape landscapes and why watershed management is critical for water security.',
    icon: WavesIcon,
  },
  {
    number: '5',
    title: 'Groundwater & Aquifer Systems: The Hidden Water Beneath Our Feet',
    subtitle: 'Everything about subsurface water and aquifer systems',
    context:
      'Groundwater is often invisible and misunderstood. This volume gives you the confidence to teach subsurface hydrology clearly.',
    items: [
      'Introduction to groundwater hydrology principles',
      'Soil moisture and the unsaturated zone',
      'The water table and saturated zone characteristics',
      'Porosity, permeability, and hydraulic conductivity',
      'Aquifer types and their characteristics',
      'Aquitards and confining layer functions',
      'Groundwater movement and hydraulic gradients',
      'Recharge and discharge area identification',
      'Springs and groundwater-dependent ecosystems',
      'Wells and groundwater extraction methods',
      'Groundwater–surface water interactions',
      'Aquifer contamination sources and pathways',
      'Overexploitation and land subsidence issues',
      'Sustainable groundwater management strategies',
      '12 practical groundwater case studies',
    ],
    result:
      'Your students understand the critical importance of groundwater and why sustainable aquifer management is essential.',
    icon: LayersIcon,
  },
  {
    number: '6',
    title: 'Water Quality & Aquatic Environments: Protecting Our Water Resources',
    subtitle: 'Understanding water quality and ecosystem health',
    context:
      'Water quality is essential but often overlooked. This volume gives you materials to teach pollution, ecosystems, and water monitoring.',
    items: [
      'Understanding water quality fundamentals',
      'Physical properties affecting water quality',
      'Chemical characteristics and water-quality indicators',
      'Biological water-quality indicators and organisms',
      'Natural factors affecting water quality',
      'Point-source and nonpoint-source pollution',
      'Agricultural, industrial, and urban contamination',
      'Nutrient enrichment and eutrophication processes',
      'Pathogens and public-health risks',
      'Freshwater ecosystems and biodiversity',
      'Water sampling and monitoring principles',
      'Interpretation of water-quality data',
      'Pollution prevention and ecosystem restoration',
      'Water-quality education and community awareness',
      '8 real-world water-quality case studies',
    ],
    result:
      'Your students understand the connections between water quality, human activities, and ecosystem health.',
    icon: LeafIcon,
  },
  {
    number: '7',
    title: 'Floods, Droughts & Hydrological Hazards: Managing Water Extremes',
    subtitle: 'Understanding and managing water-related natural hazards',
    context:
      'Hydrological hazards affect millions. This volume teaches students to understand, forecast, and respond to floods and droughts.',
    items: [
      'Introduction to hydrological hazards and risks',
      'Types and causes of flooding events',
      'River, flash, coastal, and urban flooding',
      'Flood frequency, magnitude, and probability',
      'Floodplain dynamics and vulnerable settlements',
      'Structural and non-structural flood management',
      'Drought types and development processes',
      'Meteorological, agricultural, and hydrological drought',
      'Water scarcity and drought impacts on society',
      'Risk, hazard, exposure, and vulnerability concepts',
      'Forecasting and early-warning systems',
      'Emergency planning and risk communication',
      'Nature-based hazard-reduction strategies',
      'Climate change and hydrological extremes',
      'Community preparedness and resilience building',
      '10 real-world hazard management case studies',
    ],
    result:
      'Your students understand water extremes and how communities can prepare, adapt, and build resilience.',
    icon: AlertTriangleIcon,
  },
  {
    number: '8',
    title: 'Water Resources Management & Sustainability: Securing Global Water Future',
    subtitle: 'Managing water for competing demands and future generations',
    context:
      'This is the practical heart. Your students learn about real water challenges and management solutions.',
    items: [
      'Global freshwater availability and distribution',
      'Water demand and competing uses across sectors',
      'Domestic, agricultural, and industrial water consumption',
      'Water supply systems and sanitation infrastructure',
      'Irrigation and agricultural water management',
      'Urban water systems and demand management',
      'Reservoirs, dams, and water infrastructure',
      'Hydropower and water-resource development',
      'Integrated water resources management principles',
      'Water conservation and efficiency strategies',
      'Water reuse and alternative water sources',
      'Water governance and public policy frameworks',
      'Transboundary water resources and cooperation',
      'Water conflicts and international agreements',
      'Environmental flows and ecosystem protection',
      'Sustainable and equitable water management',
      '12 real-world water management case studies',
    ],
    result:
      'Your students understand the complexity of water management and why sustainable solutions are critical.',
    icon: ShipWheelIcon,
  },
  {
    number: '9',
    title: 'Teaching Hydrology: Lesson Planning, Projects & Assessment',
    subtitle: 'Your complete guide as a hydrology educator',
    context:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year of hydrology teaching.',
    items: [
      'The educational value of hydrology in science curricula',
      'Establishing clear learning objectives',
      'Organizing hydrology content by difficulty level',
      'Designing effective lesson sequences',
      'Inquiry-based hydrology education approaches',
      'Problem-based and project-based learning strategies',
      'Using real case studies in the classroom',
      'Teaching through local and global water issues',
      'Interdisciplinary connections and links',
      'Classroom discussions and guided debates',
      'Written activities and research assignments',
      'Assessment strategies for hydrology learning',
      'Rubrics and performance criteria',
      'Addressing common hydrology misconceptions',
      'Differentiation and inclusive instruction',
      'Planning complete hydrology teaching units',
      '8 complete integrated capstone projects',
    ],
    result:
      'You\'ll never wonder "what do I teach today?" again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
